import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { scheduleService } from '@/services/api'
import { Plus, Edit, Trash2, Calendar } from 'lucide-react'
import ScheduleModal from '@/components/schedules/ScheduleModal'
import Modal from '@/components/common/Modal'
import toast from 'react-hot-toast'

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const TYPE_LABEL = {
  group: 'Класс',
  department: 'Отдел',
  individual_student: 'Ученик (инд.)',
  individual_employee: 'Сотрудник (инд.)',
}
const TYPE_BADGE = {
  group: 'badge-blue',
  department: 'badge-purple',
  individual_student: 'badge-green',
  individual_employee: 'badge-yellow',
}

const getTargetName = (s) =>
  s.group_name || (s.departments_names?.length ? s.departments_names.join(', ') : s.department_name) || '—'

function TargetDisplay({ s }) {
  const names = s.departments_names
  if (names?.length > 1) {
    return (
      <span className="flex items-center gap-1 flex-wrap">
        <span className="truncate max-w-[160px]" title={names[0]}>{names[0]}</span>
        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full shrink-0">+{names.length - 1}</span>
      </span>
    )
  }
  const text = getTargetName(s)
  return <span className="truncate max-w-[200px] block" title={text}>{text}</span>
}

export default function SchedulesPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState({ open: false, data: null })
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' })
  const [deleting, setDeleting] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => scheduleService.list().then((r) => r.data.results || r.data),
  })

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await scheduleService.delete(deleteModal.id)
      toast.success('Расписание удалено')
      qc.invalidateQueries(['schedules'])
      setDeleteModal({ open: false, id: null, name: '' })
    } catch {
      toast.error('Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  const schedules = data || []

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Расписания</h1>
          {!isLoading && (
            <p className="text-sm text-gray-400 mt-0.5">{schedules.length} расписаний</p>
          )}
        </div>
        <button className="btn-primary self-start sm:self-auto" onClick={() => setModal({ open: true, data: null })}>
          <Plus size={16} /> <span className="hidden sm:inline">Добавить расписание</span><span className="sm:hidden">Добавить</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100">
          {isLoading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="p-4 animate-pulse space-y-2">
                <div className="h-4 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))
          ) : schedules.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Calendar size={32} className="mx-auto mb-2 opacity-30" />
              <p>Расписаний не добавлено</p>
            </div>
          ) : (
            schedules.map((s) => (
              <div key={s.id} className="p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-medium text-gray-900">{s.name}</p>
                    <span className={TYPE_BADGE[s.schedule_type] || 'badge-gray'}>
                      {TYPE_LABEL[s.schedule_type] || s.schedule_type}
                    </span>
                    {s.is_active ? <span className="badge-green">Активно</span> : <span className="badge-gray">Отключено</span>}
                  </div>
                  <p className="text-xs text-gray-400" ><TargetDisplay s={s} /></p>
                  <p className="text-xs text-gray-500 font-mono mt-1">{s.start_time} – {s.end_time} · +{s.allowed_late_minutes} мин.</p>
                  <div className="flex gap-0.5 mt-2">
                    {DAYS.map((d, i) => (
                      <span key={i} className={`text-xs px-1 py-0.5 rounded ${(s.work_days || []).includes(i) ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-gray-100 text-gray-400'}`}>{d}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" onClick={() => setModal({ open: true, data: s })}><Edit size={14} /></button>
                  <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" onClick={() => setDeleteModal({ open: true, id: s.id, name: s.name })}><Trash2 size={14} /></button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Цель</th>
                <th>Начало</th>
                <th>Конец</th>
                <th>Доп. опозд.</th>
                <th>Рабочие дни</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(9).fill(0).map((_, j) => (
                      <td key={j}><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : schedules.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400">
                    <Calendar size={32} className="mx-auto mb-2 opacity-30" />
                    <p>Расписаний не добавлено</p>
                  </td>
                </tr>
              ) : (
                schedules.map((s) => (
                  <tr key={s.id}>
                    <td className="font-medium">{s.name}</td>
                    <td>
                      <span className={TYPE_BADGE[s.schedule_type] || 'badge-gray'}>
                        {TYPE_LABEL[s.schedule_type] || s.schedule_type}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">
                      <TargetDisplay s={s} />
                    </td>
                    <td className="font-mono text-sm">{s.start_time}</td>
                    <td className="font-mono text-sm">{s.end_time}</td>
                    <td className="text-sm">{s.allowed_late_minutes} мин.</td>
                    <td>
                      <div className="flex gap-0.5">
                        {DAYS.map((d, i) => (
                          <span
                            key={i}
                            className={`text-xs px-1 py-0.5 rounded ${
                              (s.work_days || []).includes(i)
                                ? 'bg-primary-100 text-primary-700 font-medium'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {s.is_active
                        ? <span className="badge-green">Активно</span>
                        : <span className="badge-gray">Отключено</span>}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          onClick={() => setModal({ open: true, data: s })}
                          title="Редактировать"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => setDeleteModal({ open: true, id: s.id, name: s.name })}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Schedule Modal */}
      <ScheduleModal
        open={modal.open}
        onClose={() => setModal({ open: false, data: null })}
        schedule={modal.data}
      />

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '' })}
        title="Удаление расписания"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteModal({ open: false, id: null, name: '' })}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Удалить расписание <strong>«{deleteModal.name}»</strong>?
        </p>
        <p className="text-sm text-gray-400 mt-2">
          Связанные записи посещаемости сохранятся.
        </p>
      </Modal>
    </div>
  )
}
